import JointAccountOwnerApiClient from "@/api/jointAccountOwnerApiClient";
import { getFullName } from "@/components/ComplexFields/PersonInformationComplexField";
import { buildComplexFieldKey, COMPLEX_FIELD_KEYS, FIELD_KEYS } from "@/constants";
import { DebitCardInformation, DigitalBankingInformation, JointAccountOwner } from "@/types";
import { DigitalBankingRole } from "@/types/enums";
import { AppToasterV2, BaseFieldProps, CoreFieldValue, hashStringSHA256 } from "@ucl/library";
import { AxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAutoSave } from "./useAutoSave";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";
import { useFinancialApplicationForm } from "./useFinancialApplicationForm";

// Browser-compatible hash function using Web Crypto API

export const useJointAccountOwner = (financialApplicationId: string, jointOwnerId: string) => {
  const [formValue, setFormValueInternal] = useState<JointAccountOwner | undefined>();
  const [formValueHash, setFormValueHash] = useState<string | undefined>();
  const debitCardInformationRenderKeyRef = useRef<string>("debit-card-information");
  const digitalBankingInformationRenderKeyRef = useRef<string>("digital-banking-information");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const jointAccountOwnerApiClient = useAuthenticatedApiClient(JointAccountOwnerApiClient);
  const { refreshFinancialApplication } = useFinancialApplicationForm(financialApplicationId);

  // Use the generic auto-save hook
  const { markDirty } = useAutoSave({
    saveFunction: async () => {
      if (formValue) {
        await saveFormData(formValue);
      }
    },
    debounceDelay: 1000,
  });

  useEffect(() => {
    getJointAccountOwner();
  }, [financialApplicationId, jointOwnerId]);

  const getJointAccountOwner = async () => {
    setIsLoading(true);
    const owner = await jointAccountOwnerApiClient.getJointAccountOwner(
      financialApplicationId,
      jointOwnerId
    );
    debitCardInformationRenderKeyRef.current = owner.debitCardInformation?.debitCardNameDisplayed
      ? "debit-card-true"
      : "debit-card-false";
    digitalBankingInformationRenderKeyRef.current = owner.digitalBankingInformation
      ?.digitalBankingUsername
      ? "digital-banking-true"
      : "digital-banking-false";
    setFormValueInternal(owner);
    const ownerHash = await hashStringSHA256(JSON.stringify(owner));
    setFormValueHash(ownerHash);
    setIsLoading(false);
  };

  const setFormValue = async (
    value: JointAccountOwner,
    _newFieldValue: CoreFieldValue,
    fieldProps: BaseFieldProps<CoreFieldValue>
  ) => {
    // Check if the form value has changed
    const newHash = await hashStringSHA256(JSON.stringify(value));
    if (!formValueHash || formValueHash === newHash) {
      return;
    }

    // Run any UI field changes
    const resolvedValue = resolveFieldForm(value, fieldProps);
    setFormValueInternal({ ...resolvedValue });
    setFormValueHash(newHash);

    // Mark as dirty and trigger auto-save
    markDirty();
  };

  const resolveFieldForm = (
    value: JointAccountOwner,
    fieldProps: BaseFieldProps<CoreFieldValue>
  ): JointAccountOwner => {
    // If Has Debit Card change to true, set Debit Card Mailing Address to Business Address
    const hasDebitCardInformationKey = buildComplexFieldKey(
      FIELD_KEYS.JOINT_ACCOUNT_OWNER.DEBIT_CARD_INFORMATION,
      COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.HAS_DEBIT_CARD
    );

    const enableClientDefaultHandling = false;

    if (
      enableClientDefaultHandling &&
      fieldProps.fieldKey === hasDebitCardInformationKey &&
      debitCardInformationRenderKeyRef.current === "debit-card-false" &&
      value.debitCardInformation?.hasDebitCard &&
      value.personInformation
    ) {
      const debitCardNameDisplayed = !value?.debitCardInformation?.debitCardNameDisplayed
        ? getFullName(value.personInformation)
        : "";
      const debitCardMailingAddress = !value?.debitCardInformation?.debitCardMailingAddress
        ? value.personInformation?.homeAddress
        : undefined;

      value.debitCardInformation.debitCardNameDisplayed = debitCardNameDisplayed;
      value.debitCardInformation.debitCardMailingAddress = debitCardMailingAddress;
      debitCardInformationRenderKeyRef.current = "debit-card-true";
    }

    // If Has Digital Banking change to true, populate digital banking fields
    const hasDigitalBankingInformationKey = buildComplexFieldKey(
      FIELD_KEYS.JOINT_ACCOUNT_OWNER.DIGITAL_BANKING_INFORMATION,
      COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.HAS_DIGITAL_BANKING
    );

    if (
      enableClientDefaultHandling &&
      fieldProps.fieldKey === hasDigitalBankingInformationKey &&
      digitalBankingInformationRenderKeyRef.current === "digital-banking-false" &&
      value.digitalBankingInformation?.hasDigitalBanking &&
      value.personInformation
    ) {
      const digitalBankingUsername = !value?.digitalBankingInformation?.digitalBankingUsername
        ? getFullName(value.personInformation).replace(/\s+/g, "")
        : "";
      const digitalBankingEmailAddress = !value?.digitalBankingInformation
        ?.digitalBankingEmailAddress
        ? value.personInformation?.emailAddress
        : "";
      const digitalBankingPhoneNumber = !value?.digitalBankingInformation?.digitalBankingPhoneNumber
        ? value.personInformation?.phoneNumber
        : "";
      const digitalBankingRole = !value?.digitalBankingInformation?.digitalBankingRole
        ? DigitalBankingRole.User
        : undefined;

      value.digitalBankingInformation.digitalBankingUsername = digitalBankingUsername;
      value.digitalBankingInformation.digitalBankingEmailAddress = digitalBankingEmailAddress;
      value.digitalBankingInformation.digitalBankingPhoneNumber = digitalBankingPhoneNumber;
      value.digitalBankingInformation.digitalBankingRole = digitalBankingRole;
      digitalBankingInformationRenderKeyRef.current = "digital-banking-true";
    }

    return { ...value };
  };

  const saveFormData = useCallback(
    async (data: JointAccountOwner) => {
      setIsSaving(true);

      try {
        const updated = await jointAccountOwnerApiClient.updateJointAccountOwner(
          financialApplicationId,
          jointOwnerId,
          data
        );
        await refreshFinancialApplication();

        // This is a temporary hack to ensure that the form value gets updated with logic from the server, but only for those parts that are server-managed.

        const updates = {
          debitCardInformation: {} as Partial<DebitCardInformation>,
          digitalBankingInformation: {} as Partial<DigitalBankingInformation>,
        };

        let mayHaveServerUpdates = false;

        if (updated.debitCardInformation?.useHomeAddressForDebitCardMailingAddress === true) {
          updates.debitCardInformation.debitCardMailingAddress =
            updated.personInformation?.homeAddress;
          mayHaveServerUpdates = true;
        }

        if (!data.debitCardInformation?.debitCardNameDisplayed) {
          updates.debitCardInformation.debitCardNameDisplayed =
            updated.debitCardInformation?.debitCardNameDisplayed;
          mayHaveServerUpdates = true;
        }

        if (!data.digitalBankingInformation?.digitalBankingUsername) {
          updates.digitalBankingInformation.digitalBankingUsername =
            updated.digitalBankingInformation?.digitalBankingUsername;
          mayHaveServerUpdates = true;
        }

        if (!data.digitalBankingInformation?.digitalBankingEmailAddress) {
          updates.digitalBankingInformation.digitalBankingEmailAddress =
            updated.digitalBankingInformation?.digitalBankingEmailAddress;
          mayHaveServerUpdates = true;
        }

        if (!data.digitalBankingInformation?.digitalBankingPhoneNumber) {
          updates.digitalBankingInformation.digitalBankingPhoneNumber =
            updated.digitalBankingInformation?.digitalBankingPhoneNumber;
          mayHaveServerUpdates = true;
        }

        if (!data.digitalBankingInformation?.digitalBankingRole) {
          updates.digitalBankingInformation.digitalBankingRole =
            updated.digitalBankingInformation?.digitalBankingRole;
          mayHaveServerUpdates = true;
        }

        if (mayHaveServerUpdates) {
          setFormValueInternal((prev) => ({
            ...prev,
            debitCardInformation: {
              ...prev?.debitCardInformation,
              ...updates.debitCardInformation,
            },
            digitalBankingInformation: {
              ...prev?.digitalBankingInformation,
              ...updates.digitalBankingInformation,
            },
          }));
        }
      } catch (error) {
        // Skip 400 errors, validation is handled by the form
        if (error instanceof AxiosError && error.response?.status === 400) {
          return;
        }
        console.error("Error saving joint account owner data:", error);

        AppToasterV2.showFailedActionToaster("Failed to save joint account owner data.");
      } finally {
        setIsSaving(false);
      }
    },
    [financialApplicationId, jointOwnerId, jointAccountOwnerApiClient]
  );

  const saveIdentityDocuments = useCallback(
    async (data: JointAccountOwner) => {
      setIsSaving(true);

      try {
        const updated = await jointAccountOwnerApiClient.updateJointAccountOwnerIdentityDocuments(
          financialApplicationId,
          jointOwnerId,
          data
        );

        return updated;
      } catch (error) {
        // Skip 400 errors, validation is handled by the form
        if (error instanceof AxiosError && error.response?.status === 400) {
          return;
        }
        console.error("Error saving joint account owner data:", error);

        AppToasterV2.showFailedActionToaster("Failed to save joint account owner data.");
      } finally {
        setIsSaving(false);
      }
    },
    [financialApplicationId, jointOwnerId, jointAccountOwnerApiClient]
  );

  return {
    formValue,
    setFormValue,
    saveFormData,
    saveIdentityDocuments,
    isSaving,
    isLoading,
    debitCardInformationRenderKey: debitCardInformationRenderKeyRef.current,
    digitalBankingInformationRenderKey: digitalBankingInformationRenderKeyRef.current,
  };
};
